import * as THREE from 'three';
import { clamp, damp } from './mathUtils';

export type CameraMode = 'chase' | 'hood' | 'cockpit';

export const CAMERA_MODES: readonly CameraMode[] = ['chase', 'hood', 'cockpit'];

export const CAMERA_LABELS: Record<CameraMode, string> = {
  chase: 'VERFOLGER',
  hood: 'MOTORHAUBE',
  cockpit: 'COCKPIT',
};

interface CameraTarget {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  /** m/s, vorzeichenbehaftet. */
  forwardSpeed: number;
  topSpeedMs: number;
  /** 0..1, wie holprig es gerade zugeht. */
  roughness: number;
}

/**
 * Weich folgende Kamera. Fahrzeugkonvention: +Z ist vorne, die Kamera sitzt
 * also bei -Z hinter dem Auto.
 */
export class CameraRig {
  mode: CameraMode = 'chase';

  private readonly pos = new THREE.Vector3(0, 5, -12);
  private readonly look = new THREE.Vector3();
  private readonly desired = new THREE.Vector3();
  private readonly desiredLook = new THREE.Vector3();
  private readonly tmp = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private fov = 60;
  private shake = 0;
  private shakeTime = 0;
  private initialized = false;

  private static readonly OFFSETS: Record<CameraMode, THREE.Vector3> = {
    chase: new THREE.Vector3(0, 2.65, -6.6),
    hood: new THREE.Vector3(0, 1.45, 1.15),
    cockpit: new THREE.Vector3(0.36, 1.28, -0.15),
  };

  cycleMode(): CameraMode {
    const idx = CAMERA_MODES.indexOf(this.mode);
    this.mode = CAMERA_MODES[(idx + 1) % CAMERA_MODES.length] ?? 'chase';
    this.initialized = false;
    return this.mode;
  }

  setMode(mode: CameraMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.initialized = false;
  }

  update(camera: THREE.PerspectiveCamera, target: CameraTarget, dt: number): void {
    const offset = CameraRig.OFFSETS[this.mode];
    this.forward.set(0, 0, 1).applyQuaternion(target.quaternion);
    this.up.set(0, 1, 0).applyQuaternion(target.quaternion);

    const speedRatio = clamp(Math.abs(target.forwardSpeed) / Math.max(target.topSpeedMs, 1), 0, 1);

    if (this.mode === 'chase') {
      // Bei Tempo weiter nach hinten und flacher - vermittelt Geschwindigkeit.
      this.tmp.copy(offset);
      this.tmp.z -= speedRatio * 1.6;
      this.tmp.y += speedRatio * 0.35;
      this.desired.copy(this.tmp).applyQuaternion(target.quaternion).add(target.position);

      // Blickpunkt leicht vor das Auto legen.
      this.desiredLook
        .copy(target.position)
        .addScaledVector(this.forward, 4.5 + speedRatio * 5)
        .addScaledVector(this.up, 0.9);

      const followRate = 6 + speedRatio * 4;
      if (!this.initialized) {
        this.pos.copy(this.desired);
        this.look.copy(this.desiredLook);
        this.initialized = true;
      }
      this.pos.x = damp(this.pos.x, this.desired.x, followRate, dt);
      this.pos.y = damp(this.pos.y, this.desired.y, followRate * 1.3, dt);
      this.pos.z = damp(this.pos.z, this.desired.z, followRate, dt);
      this.look.x = damp(this.look.x, this.desiredLook.x, 9, dt);
      this.look.y = damp(this.look.y, this.desiredLook.y, 9, dt);
      this.look.z = damp(this.look.z, this.desiredLook.z, 9, dt);
    } else {
      // Starre Kameras sitzen fest am Fahrzeug.
      this.desired.copy(offset).applyQuaternion(target.quaternion).add(target.position);
      this.desiredLook
        .copy(this.desired)
        .addScaledVector(this.forward, 12)
        .addScaledVector(this.up, this.mode === 'hood' ? -0.6 : -0.4);
      this.pos.copy(this.desired);
      this.look.copy(this.desiredLook);
      this.initialized = true;
    }

    // Kamerawackeln auf unebenem Untergrund, mit Tempo skaliert.
    const targetShake = target.roughness * (0.25 + speedRatio * 0.75);
    this.shake = damp(this.shake, targetShake, 10, dt);
    if (this.shake > 0.002) {
      this.shakeTime += dt * 26;
      const amp = this.shake * 0.055;
      this.pos.x += Math.sin(this.shakeTime * 1.7) * amp;
      this.pos.y += Math.sin(this.shakeTime * 2.3 + 1.1) * amp;
    }

    camera.position.copy(this.pos);
    camera.up.copy(this.up).lerp(WORLD_UP, 0.75).normalize();
    camera.lookAt(this.look);

    const targetFov = (this.mode === 'chase' ? 62 : 68) + speedRatio * 14;
    this.fov = damp(this.fov, targetFov, 3.5, dt);
    if (Math.abs(camera.fov - this.fov) > 0.02) {
      camera.fov = this.fov;
      camera.updateProjectionMatrix();
    }
  }

  /** Naechstes Update ohne Nachziehen platzieren (z. B. nach einem Reset). */
  snap(): void {
    this.initialized = false;
  }
}

const WORLD_UP = new THREE.Vector3(0, 1, 0);
